// De uitklapregel: het ene patroon voor alles wat openklapt (owner 12-10,
// Clipy tduwez44xawj 1:20: "zo, met de iconen, en dat je ze open kunt
// klappen"). Icoon · kop · getal rechts · pijltje dat omlaag draait; de inhoud
// klapt eronder open, op dezelfde plek (geen apart scherm).
// Ook de bronpil (het idioom van het huidige platform: uitgever + teller, een
// klik toont de bronnen) staat hier, omdat ze samen de "meer weten"-laag zijn.

import { ChevronDown, type LucideIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";

import { Icoon } from "./basis";
import { Menu } from "./menu";

export function Uitklapregel({
  icoon,
  kop,
  rechts,
  open: gestuurd,
  onOpen,
  standaardOpen = false,
  klein = false,
  children,
}: {
  icoon?: LucideIcon;
  kop: ReactNode;
  /** Rechts in de regel: een getal of een pil. */
  rechts?: ReactNode;
  /** Gestuurd van buitenaf (bv. één tegelijk open). */
  open?: boolean;
  onOpen?: (open: boolean) => void;
  standaardOpen?: boolean;
  /** Lagere regel, voor een lijst binnen een uitklapregel. */
  klein?: boolean;
  children: ReactNode;
}) {
  const [eigen, setEigen] = useState(standaardOpen);
  const open = gestuurd ?? eigen;
  const id = useId();
  const wissel = () => {
    if (gestuurd === undefined) setEigen(!open);
    onOpen?.(!open);
  };
  return (
    <div className={`s-uitklap${open ? " s-uitklap--open" : ""}${klein ? " s-uitklap--klein" : ""}`}>
      <button type="button" className="s-uitklap__knop" aria-expanded={open} aria-controls={id} onClick={wissel}>
        {icoon && <Icoon als={icoon} />}
        <span className="s-uitklap__kop">{kop}</span>
        {rechts !== undefined && <span className="s-uitklap__rechts">{rechts}</span>}
        <span className="s-uitklap__pijl">
          <Icoon als={ChevronDown} klein />
        </span>
      </button>
      {open && (
        <div id={id} className="s-uitklap__inhoud">
          {children}
        </div>
      )}
    </div>
  );
}

export type Bron = {
  sleutel: string;
  uitgever: string;
  titel?: string;
  datum?: string;
  citaat?: string;
  /** Uit de praktijk (een eigen casus of meting), niet alleen beleid. */
  praktijk?: boolean;
  onKies?: () => void;
};

/** De bronpil: "mdpi.com +7". Een klik toont de bronnen, de bovenste met zijn
 *  citaat (zoals de bronpil in het huidige platform, components/Bronpil.tsx). */
export function BronPil({ bronnen, kop, totaal }: { bronnen: readonly Bron[]; kop?: string; /** Alle bronnen, ook die niet in de lijst staan. */ totaal?: number }) {
  if (!bronnen.length) return null;
  const eerste = bronnen[0]!;
  const alle = Math.max(totaal ?? 0, bronnen.length);
  const rest = alle - 1;
  const buiten = alle - bronnen.length;
  return (
    <Menu
      knopKlasse="s-bronpil"
      label={`${alle} ${alle === 1 ? "bron" : "bronnen"}: ${eerste.uitgever}${rest ? ` en ${rest} meer` : ""}`}
      inhoud={
        <>
          <span className="s-bronpil__naam">{eerste.uitgever}</span>
          {rest > 0 && <span className="s-bronpil__tel">+{rest}</span>}
        </>
      }
    >
      {(sluit) => (
        <div className="s-bronlijst">
          <div className="s-menu__kop">{kop ?? `${alle} ${alle === 1 ? "bron" : "bronnen"}`}</div>
          {bronnen.map((b, i) => (
            <button
              key={b.sleutel}
              type="button"
              role="menuitem"
              className="s-bronlijst__regel"
              disabled={!b.onKies}
              onClick={() => {
                sluit();
                b.onKies?.();
              }}
            >
              <span className="s-bronlijst__kop">
                <strong className="s-afkap">{b.uitgever}</strong>
                {b.datum && <span className="s-bronlijst__datum">{b.datum}</span>}
                {b.praktijk && <span className="s-pil">praktijk</span>}
              </span>
              {b.titel && <span className="s-bronlijst__titel">{b.titel}</span>}
              {i === 0 && b.citaat && (
                <span className="s-bronlijst__citaat">
                  “{b.citaat.length > 200 ? `${b.citaat.slice(0, 200)}…` : b.citaat}”
                </span>
              )}
            </button>
          ))}
          {buiten > 0 && <div className="s-menu__notitie">en nog {buiten} in de kennisbank</div>}
        </div>
      )}
    </Menu>
  );
}
